/**
 * Clipboard round-trip for the questionnaire-template form (PTV2-1162) — mirrors the email-server
 * dialog. Exports the editable fields as plain (un-encoded) snake_case JSON matching the wire DTO,
 * and applies a pasted config back onto the form, accepting both snake_case and the camelCase form
 * keys. Read-only fields (id) are intentionally excluded.
 */

export interface TemplateClipboardValues {
  label: string;
  templateKey: string;
  active: boolean;
  emailSubject: string;
  emailBodyHtml: string;
  emailBodyText: string;
  emailServerId: string;
}

type ClipboardKind = 'string' | 'boolean';
interface ClipboardField {
  readonly json: string;
  readonly key: keyof TemplateClipboardValues;
  readonly kind: ClipboardKind;
}

const FIELDS: readonly ClipboardField[] = [
  { json: 'label', key: 'label', kind: 'string' },
  { json: 'template_key', key: 'templateKey', kind: 'string' },
  { json: 'active', key: 'active', kind: 'boolean' },
  { json: 'email_subject', key: 'emailSubject', kind: 'string' },
  { json: 'email_body_html', key: 'emailBodyHtml', kind: 'string' },
  { json: 'email_body_text', key: 'emailBodyText', kind: 'string' },
  { json: 'email_server_id', key: 'emailServerId', kind: 'string' },
];

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function templateValuesToJson(values: TemplateClipboardValues): Record<string, string | boolean> {
  const out: Record<string, string | boolean> = {};
  for (const field of FIELDS) {
    out[field.json] = values[field.key];
  }
  return out;
}

export function applyJsonToTemplateValues(
  prev: TemplateClipboardValues,
  parsed: Record<string, unknown>,
): { values: TemplateClipboardValues; applied: number } {
  const next = { ...prev } as Record<keyof TemplateClipboardValues, string | boolean>;
  let applied = 0;
  for (const field of FIELDS) {
    const raw = field.json in parsed ? parsed[field.json] : parsed[field.key];
    if (raw === undefined) continue;
    if (field.kind === 'boolean') {
      if (typeof raw === 'boolean') {
        next[field.key] = raw;
        applied += 1;
      }
    } else if (typeof raw === 'string') {
      next[field.key] = raw;
      applied += 1;
    }
  }
  return { values: next as unknown as TemplateClipboardValues, applied };
}
